import { ClockCircleOutlined, CloseOutlined } from "@ant-design/icons"
import { Alert } from "components/Alert/Alert"
import { Checkbox } from "components/Checkbox/Checkbox"
import {
	Form as AntdForm,
	type FormInstance,
	type FormRule,
	type NamePath,
} from "components/Form/Form"
import { Input } from "components/Input/Input"
import { Select } from "components/Select/Select"

import { ErrorCodeEnum } from "api/api"
import { DatePicker } from "components/DatePicker/DatePicker"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Modal as UIModal } from "components/Modal/Modal"
import { PhoneInput } from "components/PhoneInput/PhoneInput"
import { AlternateContact } from "interface/user"
import { Visit } from "interface/visit"
import { AlternateContactsFormItem } from "modules/patient/AlternateContactsFormItem"
import {
	normalizePhoneNumber,
	ruleUniquePhoneNumber,
	ruleValidContactInfo,
	useCheckPhoneNumberAsync,
} from "modules/patient/contact-info"
import {
	cloneElement,
	ComponentProps,
	ReactElement,
	ReactNode,
	useRef,
} from "react"
import { useTeam, useUsers } from "services/users"
import {
	useIsMutatingVisit,
	useUpdateVisit,
	useVisitsConflict,
} from "services/visits"
import { DEFAULT_VISIT_DURATION } from "utils/constants"
import { asDate, buildTimeOptions, Dayjs, today } from "utils/date"
import { clampVisitEnd, prettyVisitInterval } from "utils/visits"
import { v4 as uuidv4 } from "uuid"

export const EditVisit = Object.assign({}, { Modal, Form })

function Modal({
	children,
	...props
}: Omit<ComponentProps<typeof UIModal>, "children"> & {
	children: ReactElement<ComponentProps<typeof Form>>
}) {
	const confirmLoading = useIsMutatingVisit()
	const [form] = AntdForm.useForm()

	return (
		<UIModal
			{...props}
			title={props.title ?? "Edit information"}
			okType="primary"
			okText="Save"
			cancelText="Cancel"
			onOk={form.submit}
			confirmLoading={confirmLoading}
		>
			{cloneElement(children, { ...children.props, form })}
		</UIModal>
	)
}

function Form(props: {
	children?: ReactNode | (({ isSaving }: { isSaving: boolean }) => ReactNode)
	fields?: FormFields
	form?: FormInstance<FormValues>
	visit: Visit
	ariaLabel?: string
	onSuccess: VoidFunction
}) {
	const team = useTeam()
	const { current: formName } = useRef(`edit-visit-${uuidv4()}`)
	const {
		children,
		fields = ["agentUserIds", "notes", "patient", "visitDate"],
		visit,
		onSuccess,
	} = props
	const initialVisitDate = visit.visitDate
		? asDate(visit.visitDate)
		: asDate().startOf("hour")
	const initialValues = {
		patient: visit.patient,
		notes: visit.notes,
		agentUserIds: visit.fieldSupportAgents?.map(({ id }) => id),
		visitDate: initialVisitDate,
		visitStart: initialVisitDate.toISOString(),
		visitEnd: clampVisitEnd(
			initialVisitDate,
			initialVisitDate.add(
				visit.visitDurationInMinutes ?? DEFAULT_VISIT_DURATION,
				"minute",
			),
		),
	}
	const [form] = AntdForm.useForm<FormValues>(props.form)
	const visitStart = AntdForm.useWatch("visitStart", form)
	const visitEnd = AntdForm.useWatch("visitEnd", form)
	const agentUserIds = AntdForm.useWatch("agentUserIds", form)
	const phoneNumber = AntdForm.useWatch(fieldPaths.phoneNumberValue, form)
	const alternateContacts =
		AntdForm.useWatch<AlternateContact[]>(fieldPaths.alternateContacts, form) ??
		[]
	const isSupportsTextMessagesDisabled = (phoneNumber ?? "").length === 0
	const formAriaLabel =
		props.ariaLabel ?? `Edit information about ${visit.patient?.fullName}`
	const { data: conflictingVisits = [] } = useVisitsConflict({
		visitId: visit.id,
		agentUserIds,
		start: visitStart,
		end: visitEnd,
	})
	const { data: agents = [], isFetching } = useUsers("Field-support-agent")
	const {
		mutate: save,
		isPending: isSaving,
		isError,
	} = useUpdateVisit(visit.id)
	const { ruleCheckPhoneNumberAsync } = useCheckPhoneNumberAsync(
		visit.patient?.id,
	)
	const handleValuesChange = (
		changedValues: Partial<FormValues>,
		values: FormValues,
	) => {
		if (changedValues.visitDate) {
			const visitDateStartOfDay = changedValues.visitDate.startOf("day")
			form.setFieldsValue({
				visitStart: visitDateStartOfDay
					.hour(asDate(values.visitStart).hour())
					.minute(asDate(values.visitStart).minute())
					.toISOString(),
				visitEnd: visitDateStartOfDay
					.hour(asDate(values.visitEnd).hour())
					.minute(asDate(values.visitEnd).minute())
					.toISOString(),
			})
		} else if (changedValues.visitStart) {
			const start = asDate(changedValues.visitStart)
			const diff = asDate(visitEnd).diff(visitStart, "minute")

			form.setFieldValue(
				"visitEnd",
				clampVisitEnd(start, start.add(diff, "minute")),
			)
		} else if (changedValues.patient?.phoneNumber?.value === "") {
			form.setFieldValue(fieldPaths.phoneNumberSupports, false)
		}
	}
	const handleFinish = async ({
		visitStart,
		visitEnd,
		...values
	}: FormValues) => {
		save(
			{
				...values,
				status: visit.status,
				visitDate: visitStart,
				visitDurationInMinutes: asDate(visitEnd).diff(visitStart, "minute"),
			},
			{
				onSuccess,
				onError: (error) => {
					// Handle specific field errors
					type FieldPath =
						typeof fieldPaths.phoneNumberValue | typeof fieldPaths.email
					const fieldMap: Partial<Record<ErrorCodeEnum, FieldPath>> = {
						[ErrorCodeEnum.InvalidPhoneNumber]: fieldPaths.phoneNumberValue,
						[ErrorCodeEnum.PhoneNumberAlreadyExistsError]:
							fieldPaths.phoneNumberValue,
						[ErrorCodeEnum.EmailAlreadyExistsError]: fieldPaths.email,
					}

					const fieldPath = fieldMap[error.code as ErrorCodeEnum]

					if (fieldPath && error.message) {
						form.setFields([{ name: fieldPath, errors: [error.message] }])
						form.scrollToField(fieldPath, { behavior: "smooth" })
					}
				},
			},
		)
	}

	if (!team) {
		return null
	}

	return (
		<AntdForm<FormValues>
			form={form}
			layout="vertical"
			autoComplete="off"
			name={formName}
			aria-label={formAriaLabel}
			initialValues={initialValues}
			onFinish={handleFinish}
			onValuesChange={handleValuesChange}
		>
			{fields.includes("patient") && (
				<>
					<AntdForm.Item<FormValues>
						name={["patient", "fullName"]}
						label="Full name"
						rules={[
							...baseRules,
							{ whitespace: true },
							{
								pattern: /^[A-Za-z\s.'-]+$/,
								message: "Please enter only letters, spaces, hyphens, and dots",
							},
							{
								max: 100,
								message: "Full name cannot exceed 100 characters",
							},
						]}
					>
						<Input />
					</AntdForm.Item>
					<AntdForm.Item<FormValues>
						name={["patient", "address"]}
						label="Address"
						rules={[
							...baseRules,
							{ whitespace: true },
							{
								pattern: /^[a-zA-Z0-9\s,.'-]*$/,
								message: "Address cannot contain invalid characters",
							},
							{
								max: 300,
								message: "Address cannot exceed 300 characters",
							},
						]}
					>
						<Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
					</AntdForm.Item>
					<AntdForm.Item label="Phone number">
						<div className="flex flex-col gap-1">
							<AntdForm.Item<FormValues>
								noStyle
								name={fieldPaths.phoneNumberValue}
								dependencies={[
									fieldPaths.email,
									fieldPaths.phoneNumberSupports,
									...alternateContacts.map((_, index) => [
										...(fieldPaths.alternateContacts as NamePath),
										index,
										"phoneNumber",
										"value",
									]),
								]}
								normalize={normalizePhoneNumber}
								validateFirst
								validateDebounce={300}
								validateTrigger="onBlur"
								rules={[
									ruleValidContactInfo(
										fieldPaths.phoneNumberValue,
										fieldPaths.phoneNumberSupports,
										fieldPaths.email,
									),
									ruleUniquePhoneNumber(
										fieldPaths.phoneNumberValue,
										fieldPaths.alternateContacts,
									),
									ruleCheckPhoneNumberAsync,
								]}
							>
								<PhoneInput aria-label="Patient's phone number" />
							</AntdForm.Item>
							<AntdForm.Item<FormValues>
								noStyle
								name={fieldPaths.phoneNumberSupports}
								valuePropName="checked"
								dependencies={[fieldPaths.phoneNumberValue, fieldPaths.email]}
							>
								<Checkbox
									disabled={isSupportsTextMessagesDisabled}
									aria-label="Patient's phone supports text messages"
								>
									Supports text messages
								</Checkbox>
							</AntdForm.Item>
						</div>
					</AntdForm.Item>
					<AntdForm.Item<FormValues>
						name={fieldPaths.email}
						label="Email"
						dependencies={[["phoneNumber", "supportsTextMessages"]]}
						rules={[
							ruleValidContactInfo(
								fieldPaths.phoneNumberValue,
								fieldPaths.phoneNumberSupports,
								fieldPaths.email,
							),
							{ type: "email" },
						]}
					>
						<Input />
					</AntdForm.Item>
				</>
			)}

			{fields.includes("visitDate") && (
				<div className="flex flex-wrap items-center gap-2">
					<AntdForm.Item<FormValues>
						name="visitDate"
						label="Visit date"
						rules={baseRules}
					>
						<DatePicker
							aria-label="Visit date"
							format="MM/DD/YYYY"
							placeholder="MM/DD/YYYY"
							minDate={today()}
						/>
					</AntdForm.Item>
					<div className="flex flex-nowrap items-center gap-1">
						<AntdForm.Item<FormValues>
							name="visitStart"
							label="Start time"
							rules={baseRules}
						>
							<Select
								virtual={false}
								className="w-26"
								suffixIcon={<ClockCircleOutlined aria-hidden />}
								options={buildTimeOptions(visitStart)}
							/>
						</AntdForm.Item>
						–
						<AntdForm.Item<FormValues>
							name="visitEnd"
							label="End time"
							rules={baseRules}
						>
							<Select
								virtual={false}
								className="w-40"
								suffixIcon={<ClockCircleOutlined aria-hidden />}
								options={buildTimeOptions(visitStart, true)}
							/>
						</AntdForm.Item>
					</div>
				</div>
			)}

			{fields.includes("agentUserIds") && (
				<AntdForm.Item<FormValues>
					name="agentUserIds"
					label="Agents"
					rules={baseRules}
				>
					<Select
						allowClear
						mode="multiple"
						virtual={false}
						placeholder="Select agents"
						loading={isFetching}
						showSearch={{ optionFilterProp: "label" }}
						options={agents.map((agent) => ({
							value: agent.id,
							label: agent.fullName,
						}))}
						removeIcon={
							<CloseOutlined role="button" aria-label="Remove agent" />
						}
					/>
				</AntdForm.Item>
			)}

			{fields.includes("notes") && (
				<AntdForm.Item<FormValues>
					name="notes"
					label="Notes"
					hidden={!fields.includes("notes")}
					rules={[
						{
							max: 300,
							message: "The notes cannot exceed 300 characters",
						},
					]}
				>
					<Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
				</AntdForm.Item>
			)}

			{fields.includes("patient") && (
				<AlternateContactsFormItem
					formName={formName}
					name={["patient", "alternateContacts"]}
					title="Alternate contacts (up to 3 active)"
					permission={team.manageContacts}
					patientPhoneNumberPath={fieldPaths.phoneNumberValue}
					patientId={visit.patient?.id}
				/>
			)}

			{conflictingVisits.length > 0 && (
				<AntdForm.Item>
					<Alert
						showIcon
						type="warning"
						banner={false}
						title="Some agents have overlapping visit schedules with the current visit:"
						description={
							<ul className="flex list-disc flex-col gap-1">
								{conflictingVisits.map((visit) => (
									<li key={visit.id}>
										<span className="font-semibold">
											{prettyVisitInterval(visit)}
											{": "}
										</span>
										{visit.fieldSupportAgents
											?.filter(({ id }) => agentUserIds.includes(id))
											.map(({ fullName }) => fullName)
											.join(", ")}
									</li>
								))}
							</ul>
						}
					/>
				</AntdForm.Item>
			)}

			<ErrorStatus show={isError} scroll>
				An unexpected error occurred while scheduling the visit. Please, try
				again.
			</ErrorStatus>

			{typeof children === "function" ? children({ isSaving }) : children}
		</AntdForm>
	)
}

type FormValues = Pick<Visit, "patient" | "notes"> & {
	visitDate: Dayjs
	visitStart: string
	visitEnd: string
	agentUserIds: string[]
}

type FormFields = (keyof FormValues)[]

const fieldPaths: Record<string, NamePath<FormValues>> = {
	phoneNumberValue: ["patient", "phoneNumber", "value"],
	phoneNumberSupports: ["patient", "phoneNumber", "supportsTextMessages"],
	email: ["patient", "email"],
	alternateContacts: ["patient", "alternateContacts"],
}

const baseRules: FormRule[] = [{ required: true }]
